import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { AddUsersToAudienceSuccess, DeleteAudienceUsersSuccess, MoveAudienceUsersSuccess } from 'app/library/audience-user/actions';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-user/interfaces/audience-users-action.action-payload.interface';
import { AudienceUserExtended } from 'app/library/audience-user/models/audience-user.extended.model';
import { AudienceUser } from 'app/library/audience-user/models/audience-user.model';
import { SortAudienceUsers } from 'app/library/audience-users/actions';
import { AudienceUserDataSource } from 'app/library/audience-users/components/list/audience-datasource';
import { AudienceUsersService } from 'app/library/audience-users/services/audience-users.service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { GrowMoveAudienceMembersDialogComponent } from 'app/library/grow/dialogs/grow-move-audience-members-dialog/grow-move-audience-members-dialog.component';
import {
    AudienceMembersActionsService,
    IAudienceMembersActionsContext,
    IAudienceMembersActionsPermissions,
} from 'app/library/grow/services/audience-members-actions.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FuseConfigService } from 'app/projects/fuse/src/lib/services/config.service';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { UserStatuses } from 'app/projects/user/src/lib/models/user-statuses';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-audience-users-list',
    templateUrl: './audience-users-list.component.html',
    styleUrls: ['./audience-users-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AudienceUsersListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy, AfterViewInit {
    destroy$: Subject<boolean> = new Subject<boolean>();

    @Input()
    sortBy: string;

    @Input()
    sortOrder: SortDirection;

    @Input()
    user$: Observable<AudienceUser[]>;

    @Input()
    loadUser$: Observable<boolean>;

    @Input()
    filteredUsers$: Observable<AudienceUserExtended[]>;

    @Input()
    selectedUserId$: Observable<string[]>;

    @Input()
    searchTerm$: Observable<string>;
    searchTerm: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    audienceId: string = '';

    @Input()
    audienceName: string = '';

    @Input()
    isRootAudience: boolean;

    @Output()
    edit = new EventEmitter<AudienceUser>();

    @Output()
    select = new EventEmitter<AudienceUser>();

    @Output()
    toggle = new EventEmitter();

    profileId = this._store.selectSnapshot(UserState).me;

    fuseConfig: any;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    dataSource: AudienceUserDataSource;
    displayedColumns = ['checkbox', 'avatar', 'firstName', 'lastName', 'email', 'buttons'];
    selectedUserIds: string[] = [];
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    engageMoveUsersDialogRef: MatDialogRef<GrowMoveAudienceMembersDialogComponent>;
    userStatuses = UserStatuses;
    actionPermissions: IAudienceMembersActionsPermissions;
    private _totalCount = 0;

    /**
     * Constructor
     */
    constructor(
        private _store: Store,
        private _action$: Actions,
        public _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _fuseConfigService: FuseConfigService,
        private _audienceService: AudienceService,
        private _audienceUsersService: AudienceUsersService,
        private _audienceMembersActionsService: AudienceMembersActionsService
    ) {
        super();
        this.actionPermissions = this._audienceMembersActionsService.getPermissions();

        // Subscribe to config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.fuseConfig = config;
        });
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this.loadUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadUsersPage(resetPageIndex);
            });

        this.user$
            .pipe(
                filter((users) => !!users),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((audienceUsers) => {
                this.checkboxes = {};

                audienceUsers.map((audienceUser) => {
                    this.checkboxes[audienceUser.id] = this.selectedUserIds.includes(audienceUser.id);
                });
            });

        this.selectedUserId$
            .pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                for (const id in this.checkboxes) {
                    if (!this.checkboxes.hasOwnProperty(id)) {
                        continue;
                    }

                    this.checkboxes[id] = selectedUserIds.includes(id);
                }

                this.selectedUserIds = selectedUserIds;
            });

        this.totalCount$.pipe(takeUntil(this._unsubscribeAll)).subscribe((totalCount) => {
            this._totalCount = totalCount || 0;
        });

        this.dataSource = new AudienceUserDataSource(this.filteredUsers$, this._store, this._action$);

        this._action$.pipe(takeUntil(this.destroy$), ofActionDispatched(AddUsersToAudienceSuccess, DeleteAudienceUsersSuccess, MoveAudienceUsersSuccess)).subscribe(() => {
            this.loadUsersPage(true);
        });

        setTimeout(() => {
            this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                const itemsleft = length - pageIndex * pageSize;
                const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                const contentElementHeight = document.getElementById('container-2').offsetHeight;
                const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                setTimeout(() => {
                    document.getElementById('container-3').scrollTop = scrollTop;
                });
            });

            this.loadUsersPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAudienceUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });
        });
    }

    ngAfterViewInit(): void {
        this.searchTerm$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((searchTerm, index) => {
                    this.searchTerm = searchTerm || '';
                    return index > 0;
                })
            )
            .subscribe((searchTerm) => {
                this.loadUsersPage(true);
            });
    }

    deleteAudienceUser(audienceUser: AudienceUserExtended): void {
        this._audienceMembersActionsService.removeUsers(this._buildActionsContext(), [audienceUser.userId]);
    }

    openPasswordlessAccessDialog(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.openPasswordlessAccessDialog(this._buildActionsContext(), [audienceUser.userId]);
    }

    openAssignUserToTeamDialog(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.openAssignUserToTeamDialog(this._buildActionsContext(), [audienceUser.userId]);
    }

    assignUserToExperience(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.assignUsersToExperience(this._buildActionsContext(), [audienceUser.userId]);
    }

    assignUserToAudience(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.assignUsersToAudience(this._buildActionsContext(), [audienceUser.userId]);
    }

    assignUserToCourse(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.assignUsersToCourse(this._buildActionsContext(), [audienceUser.userId]);
    }

    resetPassword(audienceUser: AudienceUserExtended): void {
        void this._audienceMembersActionsService.resetPasswords(this._buildActionsContext(), [audienceUser.userId]);
    }

    private _buildActionsContext(): IAudienceMembersActionsContext {
        return {
            audienceId: this.audienceId,
            audienceName: this.audienceName,
            searchQuery: this.searchTerm,
            selectedUserIds: [],
            totalCount: this._totalCount,
            onAfterAction: () => this.loadUsersPage(true),
        };
    }

    /**
     * Move Member
     */
    moveMember(userId: string): void {
        this.engageMoveUsersDialogRef = this._matDialog.open(GrowMoveAudienceMembersDialogComponent, {
            disableClose: false,
            width: '680px',
            autoFocus: false,
        });
        const audiences = this._audienceService.getAudiences();
        const audienceListTree = this._audienceService.convertListToTree(audiences);
        this.engageMoveUsersDialogRef.componentInstance.title = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.SINGULAR';
        this.engageMoveUsersDialogRef.componentInstance.label = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.LABEL';
        this.engageMoveUsersDialogRef.componentInstance.entityId = this.audienceId;
        this.engageMoveUsersDialogRef.componentInstance.audienceListTree = audienceListTree[0];
        this.engageMoveUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserId$;

        this.engageMoveUsersDialogRef.afterClosed().subscribe(async (result: IManageAudienceUsersActionPayload) => {
            if (result) {
                const selectedUserIds = result.selectedUserIds.length ? result.selectedUserIds : ([{ id: userId }] as [{ id: string }]);

                const responsePayload = await this._audienceUsersService.moveAudienceUsers(result.source, result.target, selectedUserIds);
                if (responsePayload) {
                }
                /*
                const responsePayload = 
                const isUserNewlyAssigned = responsePayload.newlyAssigned.length > 0;
                const isUserAlreadyyAssigned = responsePayload.alreadyAssigned.length > 0;
                if (isUserNewlyAssigned) {
                    const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ASSIGN_USER_SUCCESS');
                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.slow
                    });
                } else if (isUserAlreadyyAssigned) {
                    const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ALREADY_ASSIGNED_USER_SUCCESS');
                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.slow
                    });
                } */
            }
        });
    }

    /** Whether the number of selected elements matches the total number of rows. */
    get isAllSelected(): boolean {
        if (this.paginator) {
            return this.paginator.pageIndex < this.paginator.getNumberOfPages() - 1
                ? this.selectedUserIds.length === this.paginator.pageSize
                : this.selectedUserIds.length === this.paginator.length - this.paginator.pageIndex * this.paginator.pageSize;
        }
    }

    loadUsersPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }
        this.dataSource.loadUsers(this.audienceId, this.searchTerm, this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex + 1);
    }

    ngOnDestroy(): void {
        this._audienceUsersService.clearAudienceUsersState();
        this.destroy$.next(true);
    }
}
